import React, { useState, useRef, useEffect } from 'react';
import { Info, CloudUpload, X, ChevronDown, Search, CircleCheck } from 'lucide-react';
import { useHistory } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import apiHelper from '../../apiHelper';
import Fuse from 'fuse.js';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import MultiSelectSearch from '../../utils/MultiSelectSearch.js';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import noImage from '../../assets/img/brand/noImage.png';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';

export default function AddBrand() {
    const history = useHistory();
    const [uploadedImage, setUploadedImage] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const [validationError, setValidationError] = useState([]);
    const [disabled, setDisabled] = useState(false);
    const [allBrands, setAllBrands] = useState([]);
    const [isParentBrandOpen, setIsParentBrandOpen] = useState(false);
    const [parentBrandSearch, setParentBrandSearch] = useState('');
    const [selectedParentBrand, setSelectedParentBrand] = useState(null);
    const [alert, setAlert] = useState(null);
    const parentBrandRef = useRef(null);
    const [activeTab] = useState("Add Brand");
    const breadcrumbs = ["Product", "Brand", activeTab];
    const [showItemAlert, setShowItemAlert] = useState(false);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const discardRef = useRef(null);
    const saveRef = useRef(null);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [errors, setErrors] = useState({});
    const [imageError, setImageError] = useState(""); // ✅ inline error text
    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
    const [isDiscarding, setIsDiscarding] = useState(false);
    
    const [formData, setFormData] = useState({
        external_id: '', // Changed from id to external_id
        brand_name: '',
        prefix: '',
        parent_brand: '',
        original_logo: null,
        status: '1',
        notes: '',
    });

    useEffect(() => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            original_logo: uploadedImage
        }));
    }, [uploadedImage]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];

            // Validate file type
            if (!['image/jpeg', 'image/png'].includes(file.type)) {
                setImageError('Upload failed — Only JPEG/PNG files below 1MB are supported');
                return;
            }

            // Validate file size
            if (file.size > MAX_FILE_SIZE) {
                setImageError('Upload failed — Only JPEG/PNG files below 1MB are supported');
                return;
            }

            // Valid file
            setUploadedImage(file);
            setImageError(null);
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (uploadedImage) {
            // Prevent dropping a new file if one already exists
            return;
        }
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];

            if (!['image/jpeg', 'image/png'].includes(file.type)) {
                setImageError('Upload failed — Only JPEG/PNG files below 1MB are supported');
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setImageError('Upload failed — Only JPEG/PNG files below 1MB are supported');
                return;
            }

            setUploadedImage(file);
            setImageError(null);
            e.dataTransfer.clearData();
        }
    };

    const handleBrowseClick = () => {
        if (uploadedImage) {
            // Prevent selecting new file if one is already uploaded
            return;
        }
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleRemoveImage = () => {
        setUploadedImage(null);
        setImageError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const toggleParentBrand = () => {
        setIsParentBrandOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isParentBrandOpen && parentBrandRef.current && !parentBrandRef.current.contains(e.target)) {
                setIsParentBrandOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isParentBrandOpen]);

    const parentBrandInputRef = useRef(null);

    useEffect(() => {
        if (isParentBrandOpen && parentBrandInputRef.current) {
            parentBrandInputRef.current.focus();
            // move caret to end
            const val = parentBrandInputRef.current.value;
            parentBrandInputRef.current.setSelectionRange(val.length, val.length);
        }
    }, [isParentBrandOpen]);

    const fuse = new Fuse(allBrands, {
        keys: ['brand_name'],
        threshold: 0.3,
        includeMatches: true,
    });

    const filteredParentBrand = parentBrandSearch
        ? fuse.search(parentBrandSearch)
        : allBrands.map((b) => ({ item: b }));

    console.log("Filtered Parent Brands:", filteredParentBrand);

    const handleParentSelect = (brand) => {
        setSelectedParentBrand(brand);
        setFormData(prev => ({
            ...prev,
            parent_brand: brand.brand_id
        }));
    };

    const showAlert = (type, action, recordName) => {
        setAlert({
            type,        // success | error | info
            action,      // create | update
            recordName,  // e.g. "Brand"
        });
        setShowItemAlert(true);
        setTimeout(() => setShowItemAlert(false), 5000);
    };

    const handleSave = async () => {
        setDisabled(true);
        let form_errors = {};

        const parentBrandId = selectedParentBrand ? selectedParentBrand.brand_id : '';
        const formDataWithParent = { ...formData, parent_brand: parentBrandId };

        if (!formDataWithParent.brand_name?.trim()) {
            form_errors.brand_name = "Brand Name is required";
        }
        if (formDataWithParent.external_id && formDataWithParent.external_id.length > 50) {
            form_errors.external_id = "";
        }

        const duplicateName = validateLocalDuplicate("brand_name", formDataWithParent.brand_name);
        const duplicateId = validateLocalDuplicate("external_id", formDataWithParent.external_id); // Changed from "id" to "external_id"

        if (duplicateName) form_errors.brand_name = "Brand Name already exists";
        if (duplicateId) form_errors.external_id = "Brand ID already exists";

        if (Object.keys(form_errors).length > 0) {
            setErrors(form_errors);
            showAlert("error", "create", "Brand");
            setDisabled(false);
            return;
        }

        try {
        const postFormData = new FormData();
        Object.keys(formDataWithParent).forEach((key) => {
            if (key === 'original_logo' && formDataWithParent[key]) {
                postFormData.append('original_logo', formDataWithParent[key]);
            } else if (formDataWithParent[key] !== '' && formDataWithParent[key] !== null) {
                postFormData.append(key, formDataWithParent[key]);
            }
        });

        const response = await apiHelper(
            'inventory/brands/create/',
            'POST',
            postFormData,
            'multipart/form-data'
        );
        setDisabled(false);

        if (response && response.ok) {
            setIsDirty(false);
            showAlert("success", "create", "Brand");
            setFormData({
                external_id: '', // Changed from id to external_id
                brand_name: '',
                prefix: '',
                parent_brand: '',
                original_logo: null,
                status: '1',
                notes: '',
            });
            setUploadedImage(null);
            setSelectedParentBrand(null);
            setErrors({});
            history.push("/brand", { success: "Brand Saved Successfully" });
            return;
        }

        if (response?.status === 400) {
            const response_errors = await response.json();
            const newErrors = {};

            if (response_errors.brand_name) {
                newErrors.brand_name = response_errors.brand_name[0];
            }
            if (response_errors.external_id) {
                newErrors.external_id = response_errors.external_id[0];
            }

            setErrors(newErrors);
            showAlert("error", "create", "Brand");
            return;
        }

        showAlert("error", "create", "Brand");
        } catch (error) {
            console.error('Save brand error:', error);
        }
    };

    const handleDiscard = () => {
        setDiscardModalOpen(true);
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            console.log("🟠 Discard confirmed, navigating to brand with state...");
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);

            setTimeout(() => {
                console.log("🟢 Navigating with success state");
                history.push("/brand", { success: "Brand Discarded Successfully" });
            }, 100);
        } else {
            setDiscardModalOpen(false);
        }
    };

    const handleCancelDiscard = () => {
        setDiscardModalOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (discardRef.current && !discardRef.current.contains(event.target)) {
                setDiscardModalOpen(false);
            }
        }
        if (discardModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [discardModalOpen]);

    useEffect(() => {
        async function fetchBrandsData() {
            try {
                const response = await apiHelper('inventory/brands/?is_archived=false', 'GET');
                if (response && response.ok) {
                    const result = await response.json();
                    const activeBrands = result.filter(b => b.status === true);
                    console.log("Fetched Brands for Parent Brand:", result);
                    setAllBrands(activeBrands);
                }
            } catch (error) {
                console.error('Failed to fetch brands:', error);
            }
        }
        fetchBrandsData();
    }, []);

    const handleSaveConfirm = () => {
        setSaveModalOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (saveRef.current && !saveRef.current.contains(event.target)) {
                setSaveModalOpen(false);
            }
        }
        if (saveModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [saveModalOpen]);

    const handleChangeWithDirty = (e) => {
        setIsDirty(true);
        handleChange(e);
    };

    // const validateLocalDuplicate = (field, value) => {
    //     if (!value?.trim()) return false;

    //     const normalized = value.trim().toLowerCase();
    //     let exists = false;

    //     if (field === "brand_name") {
    //         exists = allBrands.some(
    //             (b) => b.brand_name && b.brand_name.trim().toLowerCase() === normalized
    //         );
    //         if (exists) {
    //             setErrors((prev) => ({ ...prev, brand_name: "Brand Name already exists" }));
    //         }
    //     }

    //     if (field === "id") {
    //         exists = allBrands.some(
    //             (b) => b.id && b.id.trim().toLowerCase() === normalized
    //         );
    //         if (exists) {
    //             setErrors((prev) => ({ ...prev, id: "Brand ID already exists" }));
    //         }
    //     }

    //     return exists; // ✅ return boolean for save validation
    // };

    const validateLocalDuplicate = (field, value) => {
        if (!value?.trim()) return false;

        const normalized = value.trim().toLowerCase();
        let exists = false;

        if (field === "brand_name") {
            const currentParent = formData.parent_brand || null;
            exists = allBrands.some(
                (b) => b.brand_name && b.brand_name.trim().toLowerCase() === normalized &&
                (b.parent_brand || null) == currentParent
            );

            // 🔥 Always update error: set or clear
            setErrors((prev) => ({
                ...prev,
                brand_name: exists ? "Brand Name already exists" : ""
            }));
        }

        if (field === "external_id") { // Changed from "id" to "external_id"
            exists = allBrands.some(
                (b) => b.external_id && b.external_id.trim().toLowerCase() === normalized
            );

            // 🔥 Always update error: set or clear
            setErrors((prev) => ({
                ...prev,
                external_id: exists ? "Brand ID already exists" : "" // Changed from id to external_id
            }));
        }

        return exists;
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setIsDirty(true);
    };

    const handleFieldBlur = (e) => {
        const { name, value } = e.target;
        if (name === "brand_name" && !value.trim()) {
            setErrors((prev) => ({
                ...prev,
                brand_name: "Brand Name is required",
            }));
            return;
        }
        validateLocalDuplicate(name, value);
    };

    useEffect(() => {
        const navType =
            performance.getEntriesByType("navigation")[0]?.type ||
            (performance.navigation?.type === 1 ? "reload" : "navigate");

        console.log("Navigation Type:", navType);
    }, []);

    return (
        <>
            <div className="company-body main-company-body-inner new-look" style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}>
                {showItemAlert && alert && (
                    <AlertNotification
                        type={alert.type}
                        action={alert.action}
                        recordName={alert.recordName}
                        onClose={() => setShowItemAlert(false)}
                    />
                )}
                <div className="breadcrumb">
                    {breadcrumbs.map((crumb, index) => {
                        const isLast = index === breadcrumbs.length - 1;
                        return (
                            <span
                                key={index}
                                className={`breadcrumb-item ${crumb === "Brand" ? "clickable" : ""}`}
                                onClick={() => {
                                    if (crumb === "Brand") {
                                        history.push("/brand");
                                    }
                                }}
                                style={{ cursor: crumb === "Brand" ? "pointer" : "default" }}
                            >
                                {crumb}
                                {!isLast ? " " : ""}
                            </span>
                        );
                    })}
                </div>
                <div className='edit-organisation-info'>
                    <div className='edit-organisation-left'>
                        <h1>Add Brand</h1>
                    </div>
                    <div className='discard-save-organisation-right d-flex align-items-center gap-2'>
                        <button className="dark-button" style={{
                            display: 'flex',
                            height: '34px',
                            minWidth: '100px',
                            padding: '8px',
                            justifyContent: 'center',
                            alignContent: 'center',
                            gap: '10px',
                            borderRadius: '8px',

                        }} type='button' onClick={handleDiscard}>
                            Discard
                        </button>
                        {/* ✅ Dynamic Discard Confirmation Modal */}
                        <DiscardConfirmationModal
                            isOpen={discardModalOpen}
                            onCancel={handleCancelDiscard}
                            onConfirm={handleDiscardConfirm}
                            entityName="Brand"  // 🔁 Change dynamically (e.g., "Category", "Stock")
                        />
                        <button className="dark-border" style={{
                            display: 'flex',
                            height: '34px',
                            minWidth: '100px',
                            padding: '8px',
                            justifyContent: 'center',
                            alignContent: 'center',
                            gap: '10px',
                            borderRadius: '8px',
                            boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
                        }} type='button' onClick={handleSave}>
                            Save
                        </button>
                    </div>
                </div>
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingBottom: '24px',
                }}>
                <div className="info-section">
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header">
                            <h4 className="info-title">Basic Info</h4>
                        </div>
                        <div className="info-content d-block">
                            <div className='grid-info-adding'>
                                <div className='row gappingcls-added'>
                                    <div className='col-lg-6 col-md-6 col-12'>
                                        <div className="info-inner">
                                            <label htmlFor="brandName" className="form-label d-flex align-items-center gap-1">
                                                Brand Name <span className='error-color'>*</span>
                                            </label>

                                            <input
                                                type="text"
                                                className="form-control common-input-all"
                                                id="brandName"
                                                name="brand_name"
                                                value={formData.brand_name}
                                                onChange={handleFieldChange}
                                                onBlur={handleFieldBlur}
                                                style={errors.brand_name ? { borderColor: '#dc3545' } : { border: '1px solid #6C757D' }}
                                            />

                                            {errors.brand_name && (
                                                <div className="invalid-feedback" style={{
                                                    display: 'block',
                                                    fontSize: '12px',
                                                    color: "#DC3545",
                                                    fontFamily: "Montserrat",
                                                    fontStyle: 'normal',
                                                    fontWeight: '400px',
                                                    lineHeight: 'normal',
                                                    marginTop: '5px'
                                                }}>
                                                    {errors.brand_name}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className='col-lg-6 col-md-6 col-12'>
                                        <div className="info-inner">
                                            <label htmlFor="status" className="form-label">
                                                Status <span className='error-color'>*</span>
                                            </label>
                                            <div className='flexing-type-radio'>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="status"
                                                        id="flexRadioDefault1"
                                                        defaultChecked
                                                        value="1"
                                                        onChange={handleChangeWithDirty}
                                                    />
                                                    <label className="form-check-label" htmlFor="flexRadioDefault1" style={{ fontWeight: 'normal', fontSize: '14px' }}>
                                                        Active
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="status"
                                                        id="flexRadioDefault2"
                                                        value="0"
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="flexRadioDefault2" style={{ fontWeight: 'normal', fontSize: '14px' }}>
                                                        Inactive
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='row gappingcls-added'>
                                    <div className='col-lg-6 col-md-6 col-12'>
                                        <div className="info-inner">
                                            <label htmlFor="brandId" className="form-label d-flex align-items-center gap-2">
                                                ID {/* Label remains "ID" */}
                                                {/* <Tooltip content="A shorter, human-friendly label for your brand">
                                                    <Info size={16} className="info-icon" />
                                                </Tooltip> */}
                                            </label>
                                            <CharacterLimitedTextarea
                                                id="brandId"
                                                name="external_id" // Field name changed to external_id
                                                value={formData.external_id}
                                                onChange={handleFieldChange}
                                                onBlur={handleFieldBlur}
                                                maxLength={50}
                                                counterPosition="end"
                                                error={errors.external_id} // Changed from errors.id to errors.external_id
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header">
                            <h4 className="info-title">Business Info</h4>
                        </div>
                        <div className="info-content d-block">
                            <div className='grid-info-adding'>
                                <div className='row gappingcls-added'>
                                    <div className='col-lg-6 col-md-6 col-12'>
                                        <MultiSelectSearch
                                            label="Parent Brand"
                                            tooltip={<Tooltip title="Parent Brand" body="The main brand this brand belongs under if any. Example: Redmi's parent brand is Xiaomi"><Info size={16} className="info-icon" /></Tooltip>}
                                            options={allBrands}
                                            selected={selectedParentBrand ? [selectedParentBrand] : []} // wrap in array for MultiSelectSearch
                                            setSelected={(selectedArray) => {
                                                const selected = selectedArray[0] || null;
                                                setSelectedParentBrand(selected);
                                                const newParentId = selected ? selected.brand_id : null;
                                                setFormData(prev => ({ ...prev, parent_brand: newParentId }));
                                                if (formData.brand_name?.trim()) {
                                                    const normalized = formData.brand_name.trim().toLowerCase();
                                                    const exists = allBrands.some(
                                                        (b) => b.brand_name && b.brand_name.trim().toLowerCase() === normalized &&
                                                        (b.parent_brand || null) == newParentId
                                                    );
                                                    setErrors(prev => ({ ...prev, brand_name: exists ? "Brand Name already exists" : "" }));
                                                }
                                            }}
                                            keysToSearch={['brand_name']}
                                            idKey="brand_id"
                                            displayKey="brand_name"
                                            logoKey="original_logo_url"
                                            multiple={false}
                                            fallbackImage={noImage}
                                            subTitleFn={(item) => {
                                                const h = item.brand_hierarchy || [];
                                                return h.length >= 1 ? h.map(b => b.name).join(' >> ') : null;
                                            }}
                                        />
                                    </div>
                                    <div className='col-lg-6 col-md-6 col-12'>
                                        <div className="info-inner">
                                            <label htmlFor="prefix" className="form-label d-flex align-items-center gap-1">
                                                Prefix
                                                <Tooltip
                                                    title="Prefix"
                                                    body="A short code for the brand, used in automating SKU generation">
                                                    <Info size={16} className="info-icon" />
                                                </Tooltip>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control common-input-all"
                                                id="prefix"
                                                name="prefix"
                                                value={formData.prefix}
                                                onChange={handleChangeWithDirty}
                                                style={{ border: '1px solid #6C757D' }}
                                            />
                                        </div>
                                        {errors.prefix && <p style={{ color: 'red' }}>{errors.prefix}</p>}
                                    </div>
                                </div>
                                <div className='row gappingcls-added'>
                                    <div className='col-lg-6 col-md-6 col-12'>
                                        <div className="info-inner">
                                            <label className="form-label d-flex align-items-center gap-2 position-relative">
                                                Logo
                                                <Tooltip
                                                    title="Logo"
                                                    body="Upload the brand's logo in a square (1:1) aspect ratio for best results. Maximum file size: 1MB. Only JPEG and PNG formats are supported."
                                                >
                                                    <Info size={16} className="info-icon" />
                                                </Tooltip>

                                            </label>

                                            <div
                                                className="upload-container"
                                                onClick={handleBrowseClick}
                                                onDragOver={(e) => e.preventDefault()}
                                                onDragEnter={handleDragEnter}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}
                                                style={{
                                                    border: '2px dashed #ccc',
                                                    borderRadius: '8px',
                                                    padding: '3rem 1.5rem',
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    width: '100%',
                                                    backgroundColor: isDragging ? '#CFE2FF' : '#fff',
                                                    borderColor: isDragging ? '#0D6EFD' : '#ccc',
                                                    transition: 'background-color 0.3s ease',
                                                }}
                                            >
                                                {uploadedImage ? (
                                                    <div style={{ display: 'inline-block' }}>
                                                        <img
                                                            src={URL.createObjectURL(uploadedImage)}
                                                            alt="Preview"
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '150px',
                                                                objectFit: 'contain',
                                                                borderRadius: '4px',
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveImage();
                                                            }}
                                                            style={{
                                                                position: 'absolute',
                                                                top: '6px',
                                                                right: '10px',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                borderRadius: '0',
                                                                cursor: 'pointer',
                                                                fontSize: '34px',
                                                                padding: 0,
                                                                lineHeight: '30px',
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="upload-placeholder">
                                                        <CloudUpload />
                                                        <p style={{ margin: 0, color: '#555', fontSize: '0.9rem' }}>
                                                            Drag your image here or{' '}
                                                            <span style={{ color: '#007bff', textDecoration: 'underline' }}>
                                                                Browse
                                                            </span>
                                                        </p>
                                                    </div>
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    ref={fileInputRef}
                                                    style={{ display: 'none' }}
                                                    onChange={handleImageChange}
                                                />
                                            </div>
                                            {imageError && (
                                                <p style={{ color: "red", marginTop: "0.5rem", fontSize: "0.875rem" }}>
                                                    {imageError}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="info-card">
                        <div className="info-header">
                            <h4 className="info-title">Notes</h4>
                        </div>
                        <div className="info-content">
                            <div className="info-inner common-textarea">
                                <textarea
                                    className="form-control w-100 common-textarea-all"
                                    id="exampleFormControlTextarea1"
                                    rows="3"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChangeWithDirty}
                                    style={{ border: '1px solid #6C757D' }}
                                ></textarea>
                            </div>
                        </div>
                        <UnsavedChangesPrompt isDirty={isDirty && !isDiscarding} entityName="Brand" />
                    </div>
                </div>
                {saveModalOpen && (
                    <>
                        <div className="drawer-overlay" />
                        <div
                            className="entity-drawer modal-popup-cls"
                            ref={saveRef}
                            style={{
                                maxWidth: 650,
                                margin: "0 auto",
                                background: "white",
                                borderRadius: 12,
                                zIndex: 1050,
                                padding: 24,
                                position: "fixed",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                height: "auto",
                                right: "auto",
                                overflow: "inherit",
                            }}
                        >
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                                    <X size={20} />
                                </button>
                            </div>
                            <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
                            <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
                            <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                <button className="btn-save" onClick={handleSaveConfirm}>
                                    OK
                                </button>
                            </div>
                        </div>
                    </>
                )}
                </div>
            </div>
        </>
    );
}

